<div class="import-doc">
	<h2>Import Documentation</h2>

	<section>
		<p>
			To import subscriber data, prepare a CSV file containing either a full snapshot or delta (changes only) of
			your subscriber base.
		</p>
	</section>

	<section>
		<h3>CSV Format Guidelines</h3>
		<ul>
			<li>Each line represents one MSISDN (mobile number).</li>
			<li>Fields are separated by a pipe character: <code>|</code>.</li>
			<li>If a field contains a pipe (<code>|</code>), escape it as <code>\|</code>.</li>
			<li>Fields must not be enclosed in quotes.</li>
			<li>Use <code>NIL</code> (uppercase) for empty, unknown or optional fields that are not included.</li>
			<li>Ensure the file extension is <code>.csv</code>.</li>
		</ul>
	</section>

	<section>
		<h3>Upload Methods</h3>
		<ul>
			<li>
				<strong>Browser Upload:</strong> Navigate to <em>Manage Imports</em> in the GUI, drag your file into the
				<em>File Drop</em> area, and confirm the upload.
			</li>
			<li>
				<strong>sFTP Upload:</strong> Automate uploads by placing the files on the designated FTP drive.
			</li>
		</ul>
	</section>

	<section>
		<h3>FTP File Naming & Import Process</h3>
		<ul>
			<li>Initial upload file name format: <code>&#123;yyyy-MM-dd&#125;-&#123;file_number&#125;.csv</code><br>
				Example: <code>2025-06-01-1.csv</code>, <code>2025-06-01-2.csv</code></li>
			<li>After upload completion, rename the file to:
				<code>&#123;yyyy-MM-dd&#125;-&#123;file_number&#125;-ready.csv</code></li>
			<li>The system will automatically import the <code>-ready.csv</code> files.</li>
			<li>Rejected entries will be written to:
				<code>&#123;yyyy-MM-dd&#125;-&#123;file_number&#125;-rejected.csv</code>, including error codes.</li>
			<li>Duplicate entries already present in the system will be ignored and not logged as rejections.</li>
		</ul>
	</section>

	<section>
		<h3>Rejected File Format</h3>
		<div>
			Each rejected line will contain (All separated by a pipe character)
			<ul>
				<li>The line number (from the original file) </li>
				<li>A comma-separated error codes </li>
				<li>A comma-separated failure descriptions</li>
				<li>Followed by the line from the original file.</li>
			</ul>
		</div>
	</section>

	<section>
		<h3>CSV Field List</h3>
		<div class="row">
			<div class="col-6">
				<table class="table table-sm table-borderless table-striped">
					<thead>
						<tr>
							<th>#</th>
							<th>Field Name</th>
							<th>Description</th>
							<th>Required</th>
						</tr>
					</thead>
					<tbody>
						<tr><td>1</td>		<td>Internal reference</td>		<td>The MNO's internal reference</td>														<td>Optional</td></tr>
						<tr><td>2</td>		<td>Msisdn</td>					<td>Local or International format</td>														<td>Yes</td>	</tr>
						<tr><td>3</td>		<td>Iccid</td>					<td>&nbsp;</td>																				<td>Yes</td></tr>
						<tr><td>4</td>		<td>Sim serial</td>				<td>&nbsp;</td>																				<td>Optional</td></tr>
						<tr><td>5</td>		<td>Registration date</td>		<td>Effective start date of this entry <code>yyyy-MM-dd</code></td>							<td>Yes</td></tr>
						<tr><td>6</td>		<td>Subscriber type</td>		<td><small>enum:</small> <code>Corporate</code>, <code>Individual</code></td>				<td>Yes</td></tr>
						<tr><td>7</td>		<td>Contract type</td>			<td><small>enum:</small> <code>Postpaid</code>, <code>Prepaid</code>, <code>Hybrid</code>	<td>Yes</td></tr>
						<tr><td>8</td>		<td>Document type</td>			<td><small>enum:</small> <code>Passport</code>, <code>IdCard</code>							<td>Yes</td></tr>
						<tr><td>9</td>		<td>Document number</td>		<td>&nbsp;</td>																				<td>Yes</td></tr>
						<tr><td>10</td>		<td>ID number</td>				<td>&nbsp;</td>																				<td>Yes</td></tr>
						<tr><td>11</td>		<td>Date of issue</td>			<td><code>yyyy-MM-dd</code> eg: 2006-12-19</td>												<td>Yes</td></tr>
						<tr><td>12</td>		<td>Date of expiry</td>			<td><code>yyyy-MM-dd</code> eg: 2009-12-18</td>												<td>Optional</td></tr>
						<tr><td>13</td>		<td>Issuing country</td>		<td>ISO3 Country Code</td>																	<td>Yes</td></tr>
						<tr><td>14</td>		<td>Given names</td>			<td>Space separated list of persons names</td>												<td>Yes</td></tr>
						<tr><td>15</td>		<td>Last name</td>				<td>&nbsp;</td>																				<td>Yes</td></tr>
						<tr><td>16</td>		<td>Gender</td>					<td><small>enum:</small> <code>Male</code>, <code>Female</code>								<td>Yes</td></tr>
						<tr><td>17</td>		<td>Date of birth</td>			<td><code>yyyy-MM-dd</code> eg: 1989-05-21</td>												<td>Yes</td></tr>
						<tr><td>18</td>		<td>Nationality</td>			<td>ISO3 Country Code</td>																	<td>Yes</td></tr>
						<tr><td>19</td>		<td>Place of birth</td>			<td>&nbsp;</td>																				<td>Optional</td></tr>
						<tr><td>20</td>		<td>Country</td>				<td>&nbsp;</td>																				<td>Optional</td></tr>
						<tr><td>21</td>		<td>Sub country</td>			<td>&nbsp;</td>																				<td>Optional</td></tr>
						<tr><td>22</td>		<td>Division</td>				<td>&nbsp;</td>																				<td>Optional</td></tr>
						<tr><td>23</td>		<td>Location</td>				<td>&nbsp;</td>																				<td>Optional</td></tr>
						<tr><td>24</td>		<td>Sub location</td>			<td>&nbsp;</td>																				<td>Optional</td></tr>
						<tr><td>25</td>		<td>Minor given names</td>		<td>Space separated list of persons names</td>												<td>Required when minor</td></tr>
						<tr><td>26</td>		<td>Minor last name</td>		<td>&nbsp;</td>																				<td>Required when minor</td></tr>
						<tr><td>27</td>		<td>Minor gender</td>			<td><small>enum:</small> <code>Male</code>, <code>Female</code>								<td>Required when minor</td></tr>
						<tr><td>28</td>		<td>Minor date of birth</td>	<td><code>yyyy-MM-dd</code> eg: 2018-02-18</td>												<td>Required when minor</td></tr>
					</tbody>
				</table>
			</div>
		</div>
		<h3>Samples Data</h3>
		<div class="row">
			<ol>
				<li>REF001|27820000000|ICCID00000|SIM00000|2023-06-05|Individual|Prepaid|IdCard|P6323|ID998|1977-08-24|1987-08-21|TZA|Eva|Smith|Female|1959-08-24|UGA|Nairobi|NIL|NIL|NIL|NIL|NIL|NIL|NIL|NIL|NIL</li>
				<li>REF002|27820000001|ICCID00001|SIM00001|2023-06-09|Corporate|Postpaid|Passport|P6102|ID744|1984-12-30|1994-12-28|ZAF|Bob|Nguyen|Male|1966-12-30|TZA|Mombasa|NIL|NIL|NIL|NIL|NIL|NIL|NIL|NIL|NIL</li>
				<li>REF003|27820000002|ICCID00002|SIM00002|2023-06-16|Corporate|Postpaid|IdCard|P9511|ID555|1977-01-28|1987-01-25|KEN|David|Brown|Male|1959-01-28|UGA|Kampala|NIL|NIL|NIL|NIL|NIL|NIL|NIL|NIL|NIL</li>
				<li>REF004|27820000003|ICCID00003|SIM00003|2023-06-21|Corporate|Hybrid|Passport|P9361|ID882|1987-09-27|1997-09-25|TZA|Eva|Fawn|Female|1969-09-27|ZAF|Nairobi|NIL|NIL|NIL|NIL|NIL|NIL|NIL|NIL|NIL</li>
				<li>REF005|27820000004|ICCID00004|SIM00004|2023-06-27|Individual|Postpaid|IdCard|P4453|ID652|1976-04-22|1986-04-20|ZAF|Frank|Skye|Male|1958-04-22|KEN|Cape Town|NIL|NIL|NIL|NIL|NIL|NIL|NIL|NIL|NIL</li>
			</ol>
		</div>
	</section>
</div>
